import { useCallback, useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PageFrame } from '../components/work/page-chrome';
import { ErrorAlert, LoadingRows, PageTitle } from '../components/work/page-states';
import { useOverlays } from '../components/work/overlay-context';
import { ApiError, apiErrorMessage } from '../lib/api';
import {
  cleanRequirements,
  cleanStreams,
  fetchInitiativeTemplate,
  nameNewIds,
  requirementsProblem,
  saveInitiativeTemplate,
  streamsProblem,
  type InitiativeTemplate,
  type TemplateRequirement,
  type TemplateStream,
} from '../lib/initiative-requirements';
import { useDepartments } from '../components/work/InitiativeFields';

function move<T>(list: T[], index: number, by: number): T[] {
  const target = index + by;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

function OrderButtons({
  index,
  count,
  label,
  disabled,
  onMove,
  onRemove,
}: {
  index: number;
  count: number;
  label: string;
  disabled: boolean;
  onMove: (by: number) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex shrink-0 gap-1">
      <Button type="button" size="icon" variant="ghost" className="h-8 w-8" disabled={disabled || index === 0} aria-label={`Move ${label} up`} onClick={() => onMove(-1)}>
        <ArrowUp className="h-4 w-4" aria-hidden />
      </Button>
      <Button type="button" size="icon" variant="ghost" className="h-8 w-8" disabled={disabled || index === count - 1} aria-label={`Move ${label} down`} onClick={() => onMove(1)}>
        <ArrowDown className="h-4 w-4" aria-hidden />
      </Button>
      <Button type="button" size="icon" variant="ghost" className="h-8 w-8" disabled={disabled} aria-label={`Remove ${label}`} onClick={onRemove}>
        <Trash2 className="h-4 w-4" aria-hidden />
      </Button>
    </div>
  );
}

const NO_STREAM = '__none__';

/** The streams and starting tasks every new initiative is planned with. */
function DefaultStreamsEditor({
  value,
  disabled,
  onChange,
}: {
  value: TemplateStream[];
  disabled: boolean;
  onChange: (value: TemplateStream[]) => void;
}) {
  const projects = useDepartments();
  const known = (name: string) =>
    projects.some((p) => p.name.toLowerCase() === name.toLowerCase() || p.identifier.toLowerCase() === name.toLowerCase());
  const setEntry = (index: number, entry: TemplateStream) => onChange(value.map((e, i) => (i === index ? entry : e)));

  return (
    <section className="rounded-lg border bg-card p-4 text-card-foreground" aria-label="Default streams and tasks">
      <h2 className="text-sm font-semibold">Default streams and tasks</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Optional. New initiatives start with these under Streams &amp; additional tasks. People can change them before
        creating.
      </p>
      <ol className="mt-4 space-y-3">
        {value.map((entry, index) => {
          const missing = entry.stream.trim() !== '' && projects.length > 0 && !known(entry.stream);
          return (
            <li key={index} className="rounded-md border bg-background p-3">
              <div className="flex items-start gap-3">
                <div className="grid flex-1 gap-1.5">
                  <Label htmlFor={`ds-${index}`}>Stream</Label>
                  <Select
                    value={entry.stream || NO_STREAM}
                    disabled={disabled}
                    onValueChange={(stream) => setEntry(index, { ...entry, stream: stream === NO_STREAM ? '' : stream })}
                  >
                    <SelectTrigger id={`ds-${index}`} className="w-full sm:w-72">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_STREAM} className="text-muted-foreground">
                        Choose a stream…
                      </SelectItem>
                      {missing ? <SelectItem value={entry.stream}>{entry.stream} (not a stream yet)</SelectItem> : null}
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.name}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {missing ? (
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      There is no {entry.stream} stream, so new initiatives leave these tasks out. Pick a stream or create one.
                    </p>
                  ) : null}
                </div>
                <OrderButtons
                  index={index}
                  count={value.length}
                  label={`stream ${entry.stream || index + 1}`}
                  disabled={disabled}
                  onMove={(by) => onChange(move(value, index, by))}
                  onRemove={() => onChange(value.filter((_, i) => i !== index))}
                />
              </div>
              <div className="mt-3 space-y-2">
                {entry.tasks.map((task, taskIndex) => (
                  <div key={taskIndex} className="flex items-center gap-2">
                    <Input
                      aria-label={`${entry.stream || 'Stream'} default task ${taskIndex + 1}`}
                      value={task}
                      disabled={disabled}
                      placeholder="What needs to happen"
                      onChange={(event) =>
                        setEntry(index, { ...entry, tasks: entry.tasks.map((t, i) => (i === taskIndex ? event.target.value : t)) })
                      }
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 shrink-0"
                      disabled={disabled}
                      aria-label={`Remove ${entry.stream || 'stream'} default task ${taskIndex + 1}`}
                      onClick={() => setEntry(index, { ...entry, tasks: entry.tasks.filter((_, i) => i !== taskIndex) })}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-8 gap-1.5 text-muted-foreground"
                  disabled={disabled}
                  onClick={() => setEntry(index, { ...entry, tasks: [...entry.tasks, ''] })}
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden />
                  Add task
                </Button>
              </div>
            </li>
          );
        })}
      </ol>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="mt-3 gap-1.5"
        disabled={disabled}
        onClick={() => onChange([...value, { stream: '', tasks: [''] }])}
      >
        <Plus className="h-3.5 w-3.5" aria-hidden />
        Add stream
      </Button>
    </section>
  );
}

let draftCount = 0;

/** The business requirements every new initiative gets as launch steps. */
function RequirementsEditor({
  value,
  disabled,
  onChange,
}: {
  value: TemplateRequirement[];
  disabled: boolean;
  onChange: (value: TemplateRequirement[]) => void;
}) {
  const setEntry = (index: number, entry: TemplateRequirement) => onChange(value.map((e, i) => (i === index ? entry : e)));
  return (
    <section className="rounded-lg border bg-card p-4 text-card-foreground" aria-label="Business requirements">
      <h2 className="text-sm font-semibold">Business requirements</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Every new initiative gets each of these as a task in its launch plan. They count towards launch readiness like
        stream tasks, and anyone can give them an owner, dates and details.
      </p>
      {value.length === 0 ? (
        <p className="mt-4 rounded-md border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
          No business requirements. New initiatives start with stream tasks only.
        </p>
      ) : null}
      <ol className="mt-4 space-y-3">
        {value.map((entry, index) => (
          <li key={entry.id} className="flex items-start gap-3 rounded-md border bg-background p-3">
            <div className="grid flex-1 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor={`req-${entry.id}-title`}>Task title</Label>
                <Input
                  id={`req-${entry.id}-title`}
                  value={entry.title}
                  disabled={disabled}
                  placeholder="e.g. Pricing"
                  onChange={(event) => setEntry(index, { ...entry, title: event.target.value })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor={`req-${entry.id}-description`}>Description (optional)</Label>
                <Textarea
                  id={`req-${entry.id}-description`}
                  rows={2}
                  value={entry.description ?? ''}
                  disabled={disabled}
                  placeholder="What done looks like"
                  onChange={(event) => setEntry(index, { ...entry, description: event.target.value })}
                />
              </div>
            </div>
            <OrderButtons
              index={index}
              count={value.length}
              label={entry.title || `requirement ${index + 1}`}
              disabled={disabled}
              onMove={(by) => onChange(move(value, index, by))}
              onRemove={() => onChange(value.filter((_, i) => i !== index))}
            />
          </li>
        ))}
      </ol>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="mt-3 gap-1.5"
        disabled={disabled}
        onClick={() => {
          draftCount += 1;
          onChange([...value, { id: `new_${draftCount}`, title: '', description: null }]);
        }}
      >
        <Plus className="h-3.5 w-3.5" aria-hidden />
        Add requirement
      </Button>
    </section>
  );
}

export default function InitiativeTemplateSettings() {
  const { toast } = useOverlays();
  const [template, setTemplate] = useState<InitiativeTemplate | null>(null);
  const [requirements, setRequirements] = useState<TemplateRequirement[]>([]);
  const [streams, setStreams] = useState<TemplateStream[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const next = await fetchInitiativeTemplate();
      setTemplate(next);
      setRequirements(next.requirements);
      setStreams(next.streams);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load the initiative template'));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty =
    template !== null &&
    (JSON.stringify(requirements) !== JSON.stringify(template.requirements) ||
      JSON.stringify(streams) !== JSON.stringify(template.streams));
  const problem = requirementsProblem(requirements) ?? streamsProblem(streams);

  async function save() {
    if (!template || problem) return;
    setSaving(true);
    try {
      const next = await saveInitiativeTemplate(
        template.version,
        nameNewIds(cleanRequirements(requirements), new Set(template.requirements.map((entry) => entry.id))),
        cleanStreams(streams),
      );
      setTemplate(next);
      setRequirements(next.requirements);
      setStreams(next.streams);
      toast('Saved. New initiatives start with these requirements.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast('Another admin saved the template first. Their version is shown.');
        await load();
      } else {
        toast(apiErrorMessage(err, 'Could not save the template'));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <PageFrame>
      <div className="flex flex-1 flex-col gap-6">
        <PageTitle title="Business Requirements" />
        <p className="max-w-2xl text-sm text-muted-foreground">
          The standard template for new initiatives: title, owner and target launch, these business requirements, then
          any streams and additional tasks. Only system admins can change it. Changes apply to initiatives created
          afterwards; existing ones can add missing requirements from their launch plan.
          {template?.is_default ? ' This is the standard template; save to make it yours.' : ''}
        </p>
        {error ? <ErrorAlert>{error}</ErrorAlert> : null}
        {!template ? (
          error ? null : <LoadingRows columns={2} />
        ) : (
          <div className="flex max-w-3xl flex-col gap-4">
            <RequirementsEditor value={requirements} disabled={saving} onChange={setRequirements} />
            <DefaultStreamsEditor value={streams} disabled={saving} onChange={setStreams} />
            <div className="sticky bottom-0 z-20 flex items-center justify-end gap-3 rounded-t-lg border bg-background/95 px-4 py-3 shadow-sm backdrop-blur">
              {problem && dirty ? <p className="mr-auto text-sm text-muted-foreground">{problem}</p> : null}
              <Button
                type="button"
                variant="outline"
                disabled={!dirty || saving}
                onClick={() => {
                  setRequirements(template.requirements);
                  setStreams(template.streams);
                }}
              >
                Discard changes
              </Button>
              <Button type="button" disabled={(!dirty && !template.is_default) || Boolean(problem) || saving} onClick={() => void save()}>
                {saving ? 'Saving…' : 'Save template'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </PageFrame>
  );
}
